import { useCallback, useMemo, useRef } from 'react';
import { History } from 'lucide-react';
import {
  CellApiModule,
  CellStyleModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ColumnApiModule,
  EventApiModule,
  ModuleRegistry,
  RenderApiModule,
  RowApiModule,
  RowStyleModule,
  ScrollApiModule,
  TextEditorModule,
  TooltipModule,
  themeQuartz,
  type BodyScrollEvent,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type SortChangedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Link } from '@tanstack/react-router';
import type { ListaPrecosLinha, ListaReforma } from '@triade/contracts';
import { OrigemLucro } from '@/components/origem-lucro';
import { ChipMarca } from '@/components/marcas/marca-revisao';
import { ProfitBadge } from '@/components/profit-badge';
import { compararMcPct, SeloFaixaMc } from '@/components/margem-contribuicao';
import { formatarCusto, formatarFracaoPercentual, formatarMoeda, formatarMoedaCompleta, formatarNumeroCompleto, formatarTextoDigitado } from '@/lib/format';
import { MODULOS_VALIDACAO } from '@/lib/ag-grid-validacao';
import { cn } from '@/lib/utils';
import { ROTULO_MOTIVO, type LinhaGrade } from './tipos';

// So modulos Community, cada um explicito (nada de AllCommunityModule).
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ClientSideRowModelApiModule,
  RowApiModule,
  RowStyleModule,
  RenderApiModule,
  CellApiModule,
  ColumnApiModule,
  EventApiModule,
  ScrollApiModule,
  TextEditorModule,
  CellStyleModule,
  TooltipModule,
  ...MODULOS_VALIDACAO,
]);

const TEMA = themeQuartz.withParams({
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  headerFontSize: 12,
  headerFontWeight: 500,
  headerTextColor: 'var(--ink-muted)',
  headerBackgroundColor: 'var(--surface-muted)',
  backgroundColor: 'var(--card)',
  foregroundColor: 'var(--ink-secondary)',
  borderColor: 'var(--border)',
  accentColor: 'var(--primary)',
  rowHoverColor: 'var(--surface-muted)',
  rowHeight: 46,
  headerHeight: 36,
  wrapperBorder: false,
  wrapperBorderRadius: 0,
  cellHorizontalPadding: 12,
  inputFocusBorder: { color: 'var(--primary)' },
  inputFocusShadow: { color: 'rgba(37,99,235,0.12)', spread: 3, radius: 0 },
});

interface Contexto {
  abrirHistorico: (linha: ListaPrecosLinha) => void;
}

const ctx = (p: { context: unknown }) => p.context as Contexto;

const calculada = (d: LinhaGrade | undefined) => (d?.tipo === 'calculada' ? d.linha : undefined);

function Produto(p: ICellRendererParams<LinhaGrade>) {
  const d = p.data;
  if (!d) return null;
  const base = d.tipo === 'calculada' ? d.linha : d.pulada;
  return (
    <span className="flex min-w-0 flex-col leading-tight">
      <span className="flex items-center gap-2">
        <Link to="/cadastros/itens/$id" params={{ id: base.itemId }} tabIndex={-1} className="tabular shrink-0 font-medium text-ink hover:underline">
          {base.codigo}
        </Link>
        <span className="truncate font-medium text-ink">{base.descricao}</span>
      </span>
      <span className="truncate text-caption text-ink-muted">{ROTULO_TIPO[base.tipo]}</span>
    </span>
  );
}

const ROTULO_TIPO = {
  PRODUTO: 'Produto',
  MERCADORIA: 'Mercadoria',
  SERVICO: 'Serviço',
  SERVICO_TERCEIRO: 'Serviço de terceiro',
  MATERIA_PRIMA: 'Matéria-prima',
  MATERIAL_SECUNDARIO: 'Material secundário',
  EMBALAGEM: 'Embalagem',
  MATERIAL_AUXILIAR: 'Material auxiliar',
} as const;

/** MC% da linha (do praticado quando ha, senao do sugerido). Sem mc (versao antiga) a celula mostra "—". */
function McCelula(p: ICellRendererParams<LinhaGrade>) {
  const mc = calculada(p.data)?.mc;
  if (!mc || mc.pct === null) return <span className="text-ink-disabled">—</span>;
  return <SeloFaixaMc faixa={mc.faixa} rotulo={formatarFracaoPercentual(mc.pct)} />;
}

function Lucro(p: ICellRendererParams<LinhaGrade>) {
  const l = calculada(p.data);
  if (!l) return <span className="text-ink-disabled">—</span>;
  // Sem custo.ver a API omite o lucro de precisao cheia: o lucro % de 4 casas (exibicao) basta para a etiqueta.
  const lucroBase = l.lucroSimulado ?? l.lucroSimuladoExibicao;
  if (lucroBase === null || lucroBase === undefined) {
    // Sem lucro.ver a API manda so a faixa: a celula vira a pilula de cor, sem numero.
    if (l.faixa) return <ProfitBadge compacto faixa={l.faixa} />;
    return (
      <span className="flex flex-col items-start gap-0.5 leading-tight">
        <span className="text-ink-disabled">—</span>
        <OrigemLucro compacto desejado={l.lucroDesejadoOrigem} minimo={l.lucroMinimoOrigem} />
      </span>
    );
  }
  return (
    <span className="flex flex-col items-start gap-0.5 leading-tight">
      <ProfitBadge
        compacto
        lucro={lucroBase}
        {...(l.lucroSimuladoExibicao ? { lucroExibido: l.lucroSimuladoExibicao } : {})}
        meta={l.lucroDesejado}
        minimo={l.lucroMinimo}
        {...(l.faixa ? { faixa: l.faixa } : {})}
      />
      <OrigemLucro compacto desejado={l.lucroDesejadoOrigem} minimo={l.lucroMinimoOrigem} />
    </span>
  );
}

const ORIGEM_ROTULO = { BASE: 'base', MANUAL: 'manual', DERIVADO: 'derivado' } as const;
const ORIGEM_ESTILO = {
  BASE: 'bg-secondary text-secondary-foreground',
  MANUAL: 'bg-warning-bg text-warning-ink',
  DERIVADO: 'bg-secondary text-ink-muted',
} as const;

/** Preco do rascunho (nao publicado) desta linha: preco, origem e lucro simulado, tudo da API. */
function Rascunho(p: ICellRendererParams<LinhaGrade>) {
  const l = calculada(p.data);
  if (!l) return null;
  const r = l.rascunho;
  if (!r) return <span className="text-ink-disabled">—</span>;
  return (
    <span className="flex items-center gap-1.5 leading-tight" data-testid="rascunho-linha">
      <span className="tabular font-semibold text-ink">{formatarMoeda(r.preco)}</span>
      <span className={cn(CHIP, ORIGEM_ESTILO[r.origem])}>{ORIGEM_ROTULO[r.origem]}</span>
      <ProfitBadge compacto lucro={r.lucroSimulado} meta={l.lucroDesejado} minimo={l.lucroMinimo} faixa={r.faixa} />
    </span>
  );
}

const CHIP = 'rounded-full px-2 text-caption leading-5 font-medium whitespace-nowrap';

/** Marca "custo atualizado — revisar preco" da linha (vinda da API): o delta e o sugerido novo. So consulta. */
function Marca(p: ICellRendererParams<LinhaGrade>) {
  const m = calculada(p.data)?.marcaRevisao;
  if (!m) return null;
  return (
    <span className="flex min-w-0 items-center gap-1.5 overflow-hidden">
      <ChipMarca marca={m} className="truncate" />
    </span>
  );
}

/** Atalho para a Formacao de preco do mesmo item, tabela e condicao (a lista so consulta; quem altera e a Formacao). */
function AbrirFormacao(p: ICellRendererParams<LinhaGrade>) {
  const l = calculada(p.data);
  if (!l) return null;
  return (
    <Link
      to="/precificar/formacao"
      search={{ itemId: l.itemId, tabelaId: l.tabelaId, condicaoId: l.condicaoId }}
      tabIndex={-1}
      className="whitespace-nowrap text-small font-medium text-primary hover:underline"
    >
      Abrir na Formação
    </Link>
  );
}

function Situacao(p: ICellRendererParams<LinhaGrade>) {
  const d = p.data;
  if (!d) return null;
  if (d.tipo === 'pulada') {
    return (
      <span className="flex items-center gap-1.5" data-testid="linha-pulada" title={d.pulada.message ?? ROTULO_MOTIVO[d.pulada.reason]}>
        <span className={cn(CHIP, 'bg-danger-bg text-danger-ink')}>{ROTULO_MOTIVO[d.pulada.reason]}</span>
        {d.pulada.message && <span className="truncate text-caption text-ink-muted">{d.pulada.message}</span>}
      </span>
    );
  }
  const l = d.linha;
  return (
    <span className="flex items-center gap-1 overflow-hidden">
      {l.flags.abaixoDoSugerido && <span className={cn(CHIP, 'bg-warning-bg text-warning-ink')}>Abaixo do sugerido</span>}
      {l.flags.abaixoDoMinimo && <span className={cn(CHIP, 'bg-danger-bg text-danger-ink')}>Abaixo do mínimo</span>}
      {l.flags.semPraticado && <span className={cn(CHIP, 'bg-secondary text-secondary-foreground')}>Sem praticado</span>}
      {l.flags.semFicha === true && <span className={cn(CHIP, 'bg-secondary text-secondary-foreground')}>Sem ficha</span>}
    </span>
  );
}

function Historico(p: ICellRendererParams<LinhaGrade>) {
  const l = calculada(p.data);
  if (!l) return null;
  return (
    <button
      type="button"
      aria-label={`Histórico de praticados de ${l.codigo}`}
      title="Histórico de preços praticados"
      tabIndex={-1}
      onClick={() => ctx(p).abrirHistorico(l)}
      className="inline-flex size-7 items-center justify-center rounded-md text-ink-muted hover:bg-line-soft hover:text-ink"
    >
      <History aria-hidden="true" className="size-4" />
    </button>
  );
}

const DIREITA = { headerClass: 'ag-right-aligned-header', cellClass: 'ag-right-aligned-cell tabular' } as const;

/**
 * `comCusto` = permissao custo.ver: sem ela a API nem envia o custo e a coluna some.
 * `negociado` = politica NEGOCIADO: sem praticado, sem rascunho e sem lucro simulado. `comRascunho` = alguma linha tem rascunho.
 */
/** Coluna de reforma (valor da API por linha; sem reforma = "—"). Nada e calculado aqui. */
function colunaReforma(colId: string, headerName: string, campo: (r: ListaReforma) => string): ColDef<LinhaGrade> {
  return {
    colId,
    headerName,
    width: 132,
    suppressNavigable: true,
    valueGetter: (p) => {
      const r = calculada(p.data)?.reforma;
      return r ? formatarMoeda(campo(r)) : '—';
    },
    ...DIREITA,
  };
}

/** Praticado com a segunda linha pequena: total da NF do praticado (reforma), quando a API devolve. */
function PraticadoCelula(p: ICellRendererParams<LinhaGrade>) {
  const r = calculada(p.data)?.reforma;
  return (
    <span className="flex min-w-0 flex-col items-end leading-tight">
      <span>{p.valueFormatted ?? p.value}</span>
      {r?.praticadoTotalNf !== undefined && <span className="text-caption font-normal text-ink-muted">NF {formatarMoeda(r.praticadoTotalNf)}</span>}
    </span>
  );
}

function colunas(ordem: 'codigo' | '-codigo', comCusto: boolean, negociado: boolean, comRascunho: boolean, informativo: boolean, comLucro: boolean): ColDef<LinhaGrade>[] {
  const todas: ColDef<LinhaGrade>[] = [
    {
      colId: 'produto',
      headerName: 'Produto',
      flex: 1,
      minWidth: 240,
      suppressNavigable: true,
      sortable: true,
      sort: ordem === '-codigo' ? 'desc' : 'asc',
      sortingOrder: ['asc', 'desc'],
      // A ordem vem do servidor; o comparador neutro mantem a ordem das linhas como a API devolveu.
      comparator: () => 0,
      cellRenderer: Produto,
    },
    {
      colId: 'tabela',
      headerName: 'Tabela',
      width: 84,
      suppressNavigable: true,
      valueGetter: (p) => (p.data?.tipo === 'calculada' ? p.data.linha.tabelaCodigo : p.data?.pulada.tabelaCodigo),
    },
    {
      colId: 'custo',
      headerName: 'Custo',
      width: 124,
      suppressNavigable: true,
      valueGetter: (p) => (p.data?.tipo === 'calculada' && p.data.linha.unitCost !== undefined ? formatarCusto(p.data.linha.unitCost) : '—'),
      ...DIREITA,
    },
    {
      colId: 'sugerido',
      headerName: 'Sugerido',
      width: 116,
      suppressNavigable: true,
      valueGetter: (p) => (p.data?.tipo === 'calculada' ? formatarMoeda(p.data.linha.sugeridoExibicao) : '—'),
      tooltip: (p) => (p.data?.tipo === 'calculada' ? `Preço sugerido a prazo com IPI: ${p.data.linha.sugerido}` : undefined),
      ...DIREITA,
    },
    colunaReforma('reformaSemTributos', 'Preço (sem CBS/IBS)', (r) => r.precoSemTributos),
    colunaReforma('reformaCbsIbs', informativo ? '+ CBS/IBS (destacado, não soma)' : '+ CBS/IBS', (r) => r.cbsIbs),
    colunaReforma('reformaTotalNf', 'Total da NF', (r) => r.totalNf),
    {
      colId: 'marca',
      headerName: 'Revisar preço',
      width: 268,
      suppressNavigable: true,
      cellRenderer: Marca,
    },
    {
      colId: 'praticado',
      headerName: 'Praticado (R$)',
      width: 132,
      suppressNavigable: true,
      valueGetter: (p) => {
        const l = calculada(p.data);
        if (!l || l.praticado === null) return '';
        return formatarNumeroCompleto(l.praticado); // valor completo; o formatador mostra nas casas da empresa
      },
      valueFormatter: (p) => (p.value === '' || p.value == null ? '—' : formatarTextoDigitado(String(p.value), 'dinheiro')),
      cellRenderer: PraticadoCelula,
      tooltip: (p) => {
        const l = calculada(p.data);
        if (!l) return undefined;
        return l.praticadoEm ? `Registrado em ${l.praticadoEm} · ${formatarMoedaCompleta(l.praticado)}` : 'Sem preço praticado publicado';
      },
      headerClass: 'ag-right-aligned-header',
      cellClass: (p) => {
        const l = calculada(p.data);
        return cn(
          'ag-right-aligned-cell tabular font-semibold',
          l && l.praticado !== null && !l.flags.abaixoDoSugerido && 'text-success-ink',
          l?.flags.abaixoDoSugerido && 'text-warning-ink',
          l?.praticado === null && 'text-ink-disabled',
        );
      },
    },
    {
      colId: 'rascunho',
      headerName: 'Rascunho (não publicado)',
      width: 268,
      suppressNavigable: true,
      cellRenderer: Rascunho,
      headerClass: 'ag-right-aligned-header',
      cellClass: 'ag-right-aligned-cell',
    },
    {
      colId: 'lucro',
      headerName: 'Lucro simulado',
      width: 175,
      suppressNavigable: true,
      cellRenderer: Lucro,
    },
    {
      colId: 'mc',
      headerName: 'MC%',
      width: 124,
      suppressNavigable: true,
      // Ordena so as linhas carregadas (o servidor ordena apenas por produto).
      sortable: true,
      sortingOrder: ['desc', 'asc'],
      valueGetter: (p) => calculada(p.data)?.mc?.pct ?? null,
      comparator: (a: string | null, b: string | null) => compararMcPct(a, b),
      cellRenderer: McCelula,
      tooltip: (p) => {
        const mc = calculada(p.data)?.mc;
        if (!mc) return undefined;
        const base = mc.base === 'PRATICADO' ? 'do praticado' : 'do sugerido';
        return mc.valor === undefined ? `Margem de contribuição ${base}` : `Margem de contribuição ${base}: ${formatarMoeda(mc.valor)}`;
      },
      ...DIREITA,
    },
    { colId: 'situacao', headerName: 'Situação', flex: 1, minWidth: 200, suppressNavigable: true, cellRenderer: Situacao },
    { colId: 'historico', headerName: '', width: 56, suppressNavigable: true, cellRenderer: Historico },
    { colId: 'formacao', headerName: '', width: 150, suppressNavigable: true, cellRenderer: AbrirFormacao },
  ];
  const ocultas = new Set<string>([
    ...(!comCusto ? ['custo'] : []),
    ...(negociado ? ['praticado', 'rascunho', 'lucro'] : []),
    ...(!comRascunho ? ['rascunho'] : []),
    // Sem lucro.ver a API omite MC: a coluna some. O lucro fica so como pilula de cor (ver Lucro).
    ...(!comLucro ? ['mc'] : []),
  ]);
  return todas.filter((c) => !ocultas.has(c.colId as string));
}

export interface GradePrecosProps {
  linhas: LinhaGrade[];
  /** Tem custo.ver: mostra a coluna Custo (a API omite o custo de quem nao tem). Padrao: sim. */
  comCusto?: boolean;
  /** Tem lucro.ver: sem ele a API omite lucro, meta e MC; o lucro vira so cor e a coluna MC some. Padrao: sim. */
  comLucro?: boolean;
  ordem: 'codigo' | '-codigo';
  /** Ha mais paginas no servidor e nenhuma esta sendo buscada: a grade pede a proxima ao chegar perto do fim. */
  podeCarregarMais: boolean;
  onOrdem: (ordem: 'codigo' | '-codigo') => void;
  onCarregarMais: () => void;
  onHistorico: (linha: ListaPrecosLinha) => void;
  /** Politica NEGOCIADO (GET /precificacao/politica): esconde praticado, rascunho e lucro simulado. */
  negociado?: boolean;
}

/**
 * Lista de precos (AG Grid Community), SO LEITURA. Virtualizada: so as linhas visiveis vao ao DOM; as paginas chegam do servidor
 * por cursor. A grade nao calcula: sugerido, lucro e sinalizadores sao da API. Para alterar um preco, a Formacao de preco.
 */
export function GradePrecos({
  linhas, comCusto = true, comLucro = true, ordem, podeCarregarMais, onOrdem, onCarregarMais, onHistorico, negociado = false,
}: GradePrecosProps) {
  const api = useRef<GridApi<LinhaGrade> | null>(null);
  const aoHistoricoRef = useRef(onHistorico);
  aoHistoricoRef.current = onHistorico;

  const contexto = useMemo<Contexto>(() => ({ abrirHistorico: (l) => aoHistoricoRef.current(l) }), []);
  const comRascunho = useMemo(() => linhas.some((d) => d.tipo === 'calculada' && d.linha.rascunho !== undefined), [linhas]);
  const informativo = useMemo(() => linhas.some((d) => d.tipo === 'calculada' && d.linha.reforma?.informativo === true), [linhas]);
  const defs = useMemo(() => colunas(ordem, comCusto, negociado, comRascunho, informativo, comLucro), [ordem, comCusto, negociado, comRascunho, informativo, comLucro]);

  const aoRolar = useCallback(
    (e: BodyScrollEvent<LinhaGrade>) => {
      if (!podeCarregarMais) return;
      const ultima = e.api.getLastDisplayedRowIndex();
      if (ultima >= linhas.length - 8) onCarregarMais();
    },
    [podeCarregarMais, linhas.length, onCarregarMais],
  );

  const aoOrdenar = (e: SortChangedEvent<LinhaGrade>) => {
    const estado = e.api.getColumnState().find((c) => c.colId === 'produto');
    // Sem ordenacao na coluna Produto (ex.: ordenou MC%): so as linhas carregadas mudam; a ordem da API fica.
    if (!estado?.sort) return;
    const nova = estado?.sort === 'desc' ? '-codigo' : 'codigo';
    if (nova !== ordem) onOrdem(nova);
  };

  const aoPronta = (e: GridReadyEvent<LinhaGrade>) => {
    api.current = e.api;
  };

  return (
    <div
      data-testid="grade-precos"
      className="h-[560px] w-full [&_.ag-cell]:flex [&_.ag-cell]:items-center [&_.ag-right-aligned-cell]:justify-end"
    >
      <AgGridReact<LinhaGrade>
        theme={TEMA}
        rowData={linhas}
        columnDefs={defs}
        context={contexto}
        getRowId={(p) => p.data.chave}
        onGridReady={aoPronta}
        getRowStyle={(p) => (p.data?.tipo === 'calculada' && p.data.linha.rascunho ? { backgroundColor: 'color-mix(in srgb, var(--primary) 6%, transparent)' } : undefined)}
        onBodyScroll={aoRolar}
        onSortChanged={aoOrdenar}
        suppressColumnVirtualisation
        suppressMovableColumns
        tooltipShowDelay={400}
        noRowsOverlayComponent={() => null}
        defaultColDef={{ sortable: false, resizable: false, suppressHeaderMenuButton: true }}
      />
    </div>
  );
}
